import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import D2Shell from './components/d2/D2Shell';
import { Spinner } from './ui';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import { lazyWithReload } from './utils/lazyWithReload';

const LoginPage          = lazyWithReload(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazyWithReload(() => import('./pages/ForgotPasswordPage'));
const InviteAcceptPage   = lazyWithReload(() => import('./pages/InviteAcceptPage'));
const Dashboard          = lazyWithReload(() => import('./pages/Dashboard'));
const Dashboard2         = lazyWithReload(() => import('./pages/Dashboard2'));
const AssetsPage         = lazyWithReload(() => import('./pages/AssetsPage'));
const NewAsset           = lazyWithReload(() => import('./pages/NewAsset'));
const EditAsset          = lazyWithReload(() => import('./pages/EditAsset'));
const Alerts             = lazyWithReload(() => import('./pages/Alerts'));
const ScanDetail         = lazyWithReload(() => import('./pages/ScanDetail'));
const HistoryPage        = lazyWithReload(() => import('./pages/HistoryPage'));
const BatchDetailPage    = lazyWithReload(() => import('./pages/BatchDetailPage'));
const UsersPage          = lazyWithReload(() => import('./pages/UsersPage'));
const UserDetail         = lazyWithReload(() => import('./pages/UserDetail'));
const ForbiddenPage      = lazyWithReload(() => import('./pages/ForbiddenPage'));
const DomainAnalysisPage = lazyWithReload(() => import('./pages/DomainAnalysisPage'));
const IpAnalysisPage     = lazyWithReload(() => import('./pages/IpAnalysisPage'));
const FileHashPage       = lazyWithReload(() => import('./pages/FileHashPage'));
const SettingsPage       = lazyWithReload(() => import('./pages/SettingsPage'));
const AdminPanelPage     = lazyWithReload(() => import('./pages/AdminPanelPage'));
const OrgDetailPage      = lazyWithReload(() => import('./pages/OrgDetailPage'));
const ApiTokensPage      = lazyWithReload(() => import('./pages/ApiTokensPage'));
const ApiUsagePage       = lazyWithReload(() => import('./pages/ApiUsagePage'));
const ApiDocsPage        = lazyWithReload(() => import('./pages/ApiDocsPage'));
const ApiDocumentsPage   = lazyWithReload(() => import('./pages/ApiDocumentsPage'));
const AdminApiTokensPage = lazyWithReload(() => import('./pages/AdminApiTokensPage'));
const RansomwarePage     = lazyWithReload(() => import('./pages/RansomwarePage'));
const CyberBulletinPage  = lazyWithReload(() => import('./pages/CyberBulletinPage'));
const MitrePage          = lazyWithReload(() => import('./pages/MitrePage'));
const OrgManagementPage  = lazyWithReload(() => import('./pages/OrgManagementPage'));

const OrganizationModulePage = lazyWithReload(() => import('./pages/OrganizationModulePage'));

function RouteFallback() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner />
    </div>
  );
}

export default function App() {
  return (
    <ChunkErrorBoundary>
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/invite/accept" element={<InviteAcceptPage />} />
            <Route path="/403" element={<ForbiddenPage />} />
            <Route path="/dashboard2" element={<Navigate to="/dashboard" replace />} />

            {/* "Exposure Command" theme — shared shell (console rail + tabs +
                footer + chat), one instance for the whole route group. */}
            <Route element={<ProtectedRoute><D2Shell /></ProtectedRoute>}>
              <Route path="/dashboard" element={<Dashboard2 />} />

              <Route path="/assets" element={<ProtectedRoute permission="asset:list"><AssetsPage /></ProtectedRoute>} />
              <Route path="/assets/new" element={<ProtectedRoute permission="asset:create"><NewAsset /></ProtectedRoute>} />
              <Route path="/assets/:id/edit" element={<ProtectedRoute permission="asset:edit"><EditAsset /></ProtectedRoute>} />

              <Route path="/alerts" element={<ProtectedRoute permission="intelligence:query"><Alerts /></ProtectedRoute>} />
              <Route path="/scans" element={<ProtectedRoute permission="scan:list"><HistoryPage /></ProtectedRoute>} />
              <Route path="/scans/batch/:batchId" element={<ProtectedRoute permission="scan:list"><BatchDetailPage /></ProtectedRoute>} />
              <Route path="/scans/:id" element={<ProtectedRoute permission="scan:view"><ScanDetail /></ProtectedRoute>} />

              <Route path="/ransomware" element={<ProtectedRoute permission="ransomware:view"><RansomwarePage /></ProtectedRoute>} />
              <Route path="/cyber-bulletin" element={<ProtectedRoute permission="ransomware:view"><CyberBulletinPage /></ProtectedRoute>} />
              <Route path="/cyber-bulletin/:section" element={<ProtectedRoute permission="ransomware:view"><CyberBulletinPage /></ProtectedRoute>} />

              <Route path="/users" element={<ProtectedRoute permission="user:list"><UsersPage /></ProtectedRoute>} />
              <Route path="/users/:id" element={<ProtectedRoute permission="user:list"><UserDetail /></ProtectedRoute>} />

              {/* User preferences (theme, profile) — available to any authenticated user */}
              <Route path="/settings" element={<SettingsPage />} />
              {/* Admin uses the same themed shell as the rest of the workspace. */}
              <Route path="/admin-panel" element={<ProtectedRoute permission="settings:edit"><AdminPanelPage /></ProtectedRoute>} />
              <Route path="/my-organization" element={<OrgManagementPage />} />
              <Route path="/alerts/settings" element={<ProtectedRoute permission="notification:manage"><OrganizationModulePage module="alerts" /></ProtectedRoute>} />
              <Route path="/reports/settings" element={<ProtectedRoute permission="notification:manage"><OrganizationModulePage module="reports" /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute permission="notification:manage"><OrganizationModulePage module="history" /></ProtectedRoute>} />
              <Route path="/rule-manager" element={<ProtectedRoute permission="whitelist:list"><OrganizationModulePage module="rules" /></ProtectedRoute>} />
            </Route>

            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard-classic" element={<Dashboard />} />

              <Route path="/domain-analysis" element={<DomainAnalysisPage />} />
              <Route path="/ip-analysis" element={<IpAnalysisPage />} />
              <Route path="/hash-analysis" element={<FileHashPage />} />
              <Route path="/mitre" element={<ProtectedRoute><MitrePage /></ProtectedRoute>} />

              {/* MSSP: Organizations management */}
              <Route path="/organizations/:id" element={<ProtectedRoute permission="asset:list"><OrgDetailPage /></ProtectedRoute>} />

              {/* Public API — token management & usage (org admin / platform admin) */}
              <Route path="/api-tokens" element={<ProtectedRoute permission="token:manage"><ApiTokensPage /></ProtectedRoute>} />
              <Route path="/api-usage"  element={<ProtectedRoute permission="token:manage"><ApiUsagePage /></ProtectedRoute>} />
              <Route path="/api-docs"       element={<ApiDocsPage />} />
              <Route path="/api-documents" element={<ApiDocumentsPage />} />
              <Route path="/admin/api"  element={<ProtectedRoute permission="token:manage"><AdminApiTokensPage /></ProtectedRoute>} />

            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
    </ChunkErrorBoundary>
  );
}
